import { Alert, AlertTitle, Box, Grid, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import GCPConnectForm from "./GCPConnectForm";
import GCP_1 from "./images/GCP_1.png";
import GCP_2 from "./images/GCP_2.png";
import GCP_3 from "./images/GCP_3.png";

const stepContentSx = {
  display: "flex",
  flexDirection: "column",
  gap: "10px",
};

const GCPIntegration = () => {
  const [searchParams] = useSearchParams();
  const guideMode = searchParams.has("viewonly");
  const queryClient = useQueryClient();
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("gcp");
  const {
    isIntegrated: isGoogleIntegrated,
    isIntegratedLoading: isGoogleLoading,
  } = useIsIntegrated("google");
  const { removeIntegration, fetchGcpConfiguration } = useAPI();
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const clientId = "106322758873526201732";

  const { data: gcpConfiguration } = useQuery({
    queryKey: [...integrationKeys.all, "gcp", "configuration"],
    queryFn: fetchGcpConfiguration,
    select: (response) => response.data,
  });

  const gcpCloudPlatformReadonlyScope =
    gcpConfiguration?.cloud_platform_readonly_scope;

  const handleDisconnect = async () => {
    try {
      await removeIntegration("gcp");
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      Toast.success("GCP disconnected");
    } catch (error) {
      if (error.response) {
        Toast.error(`Something went wrong: ${error.response.data.detail}`);
      } else {
        Toast.error("An error occurred while processing the request.");
      }
    }
  };

  return (
    <Box>
      <Guideline
        title={
          isIntegrated
            ? "GCP is connected"
            : "Connect to get visibility into your GCP organization."
        }
        image={logos.GCPLogo}
        name={"Google Cloud Platform"}
        iconBorderRadius={0}
        iconWidth={60}
        iconHeight={30}
        content={
          isIntegratedLoading || isGoogleLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated ? (
            <Box display="grid" gap="12px">
              {guideMode && <IntegrationGuideAlert />}
              <Typography variant="body2">
                Breeze successfully connected to GCP. You can disconnect and
                reconnect to rotate credentials.
              </Typography>
              {!guideMode && (
                <Box display="flex" justifyContent="center">
                  <SecondaryButton
                    type="button"
                    onClick={handleDisconnect}
                    size="medium"
                  >
                    Disconnect
                  </SecondaryButton>
                </Box>
              )}
            </Box>
          ) : (
            <Box display="grid" gap="12px">
              {guideMode && <IntegrationGuideAlert />}
              {!isGoogleIntegrated && (
                <Alert severity="warning">
                  <AlertTitle>Google Workspace integration required</AlertTitle>
                  <Typography variant="body2">
                    To connect GCP, you must first connect Google Workspace so
                    Breeze can retrieve a super admin email.
                  </Typography>
                  {!guideMode && (
                    <Box mt="12px">
                      <SecondaryButton
                        component={Link}
                        to="/inventory/integrations/google"
                      >
                        Connect Google Workspace
                      </SecondaryButton>
                    </Box>
                  )}
                </Alert>
              )}
              {isGoogleIntegrated && (
                <Alert severity="info">
                  <AlertTitle>Google Workspace is connected</AlertTitle>
                  <Typography variant="body2">
                    Breeze will use your Google Workspace integration to
                    establish a connection to GCP.
                  </Typography>
                </Alert>
              )}
              <Box sx={{ mt: "12px", mb: "20px" }}>
                <Grid container spacing={2} direction={"row"}>
                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              <GuidelineStepByStep>
                <GuidelineStep title="Login to the Google Admin console">
                  <Box sx={stepContentSx}>
                    <Typography variant="body1">
                      1. In the Google Admin console, go to{" "}
                      <strong>Security</strong> →{" "}
                      <strong>Access and data control</strong> →{" "}
                      <strong>API controls</strong>.
                    </Typography>
                  </Box>
                </GuidelineStep>
                <GuidelineStep title="Click on MANAGE DOMAIN WIDE DELEGATION">
                  <Box sx={stepContentSx}>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: GCP_1,
                          altext: "Google Workspace Admin Console",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </Box>
                </GuidelineStep>
                <GuidelineStep title="Add a new or edit a current Breeze Scope if it exists">
                  <Box sx={stepContentSx}>
                    <Typography variant="body1">
                      If there is no Breeze Scope, add with the following Client
                      ID:{" "}
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          verticalAlign: "middle",
                          gap: 0.5,
                        }}
                      >
                        <strong>{clientId}</strong>
                        <CustomCopyButton
                          copyTitle="Client ID"
                          copyText={clientId}
                        />
                      </Box>
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: GCP_2,
                          altext: "GWS Security API Controls",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </Box>
                </GuidelineStep>
                <GuidelineStep title="Add the required scope">
                  <Box sx={stepContentSx}>
                    <Typography variant="body1">
                      Add the following scope in an empty row on the bottom:
                      <br></br>
                      <ul>
                        <li>
                          <strong>{gcpCloudPlatformReadonlyScope}</strong>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              verticalAlign: "middle",
                              ml: 0.5,
                            }}
                          >
                            <CustomCopyButton
                              copyTitle=""
                              copyText={gcpCloudPlatformReadonlyScope}
                            />
                          </Box>
                        </li>
                      </ul>
                      Click "Authorize" to add and save.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: GCP_3,
                          altext: "GWS Security API Controls",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>

              {isGoogleIntegrated && (
                <Box sx={{ mt: "12px" }} width={700}>
                  <GCPConnectForm />
                </Box>
              )}
            </Box>
          )
        }
      />
    </Box>
  );
};

export default GCPIntegration;
